import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule, ROUTES } from '@angular/router';

import {
  AccessDeniedComponent,
  HomepageRedirectGuard,
  NotFoundComponent,
  ServerErrorComponent,
  AccessCodeComponent,
} from '@digital-platform/ries-hub-ui/components';
import { FeatureFlagsService } from '@digital-platform/shared/services';
import { FeatureFlagGuard } from '@digital-platform/shared/ui';
import {
  AuthGuard,
  CreateAccountGuard,
  UniversalLoginGuard,
} from '@digital-platform/users/services';
import { RiesHubUiShellComponent } from './ries-hub-ui-shell/ries-hub-ui-shell.component';

/**
 * Allow use of injected services when preparing routes
 * @see https://www.linkedin.com/pulse/how-make-route-load-different-components-based-async-veloso-alves/
 */
@NgModule({
  declarations: [],
  imports: [CommonModule, RouterModule],
  exports: [RouterModule],
  providers: [
    {
      /**
       * TODO: Look to see if there's a better way to do this, as ROUTES is a low-level API
       * @see https://angular.io/api/router/ROUTES
       */
      provide: ROUTES,
      useFactory: setRoutesBasedOnFeatureFlags,
      deps: [FeatureFlagsService],
      multi: true,
    },
  ],
})
export class RiesHubUiShellRoutingModule {}

function setRoutesBasedOnFeatureFlags(featureFlags: FeatureFlagsService) {
  return [
    {
      path: '',
      component: RiesHubUiShellComponent,
      children: [
        {
          path: 'invite-user',
          loadChildren: () =>
            import('@digital-platform/users/feature-invite-code').then(
              (m) => m.UsersFeatureInviteCodeModule
            ),
        },
        {
          path: 'create-account',
          canActivate: [UniversalLoginGuard],
          data: {
            title: 'Create Account',
            featureFlags: 'create-account-enabled',
          },
          canActivateChild: [FeatureFlagGuard, CreateAccountGuard],
          loadChildren: () =>
            import(
              '@digital-platform/ries-hub-ui/feature-create-account-page'
            ).then((m) => m.RiesHubUiFeatureCreateAccountPageModule),
        },
        {
          path: 'change-password',
          data: {
            title: 'Change Password',
          },
          loadChildren: () =>
            import(
              '@digital-platform/ries-hub-ui/feature-change-password-page'
            ).then((m) => m.RiesHubUiFeatureChangePasswordPageModule),
        },
        {
          path: 'login',
          canActivate: [UniversalLoginGuard],
          data: {
            title: 'Login',
          },
          loadChildren: () =>
            import('@digital-platform/ries-hub-ui/feature-login-page').then(
              (m) => m.RiesHubUiFeatureLoginPageModule
            ),
        },
        {
          path: 'forgot-password',
          data: {
            title: 'Forgot Password',
          },
          loadChildren: () =>
            import(
              '@digital-platform/ries-hub-ui/feature-forgot-password-page'
            ).then((m) => m.RiesHubUiFeatureForgotPasswordPageModule),
        },
        {
          path: 'forgot-password-submitted',
          data: {
            title: 'Forgot Password Submitted',
          },
          loadChildren: () =>
            import(
              '@digital-platform/ries-hub-ui/feature-forgot-password-submitted-page'
            ).then((m) => m.RiesHubUiFeatureForgotPasswordSubmittedPageModule),
        },
        {
          path: 'verify-email',
          data: {
            title: 'Verify Email',
          },
          loadChildren: () =>
            import('@digital-platform/users/feature-verify-email-page').then(
              (m) => m.UsersFeatureVerifyEmailPageModule
            ),
        },
        {
          path: '',
          canActivate: [AuthGuard, HomepageRedirectGuard],
          children: [],
        },
        {
          path: 'dashboard',
          canActivateChild: [AuthGuard],
          data: {
            role: 'Customer',
            title: 'Dashboard',
            pageName: 'Dashboard',
          },
          loadChildren: () =>
            import('@digital-platform/ries-hub-ui/feature-dashboard-page').then(
              (m) => m.RiesHubUiFeatureDashboardPageModule
            ),
        },
        {
          path: 'waste-profiles',
          data: {
            title: 'Waste Profiles',
          },
          redirectTo: 'waste-profiles/summary',
          pathMatch: 'full',
        },
        {
          path: 'waste-profiles/summary',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'list:waste_profile',
            title: 'Waste Profiles',
            pageName: 'Waste Profiles',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-dashboard-page'
            ).then((m) => m.WasteProfilesFeatureDashboardModule),
        },
        {
          path: 'waste-profiles/duplicate',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'duplicate:waste_profile',
            title: 'Duplicate Waste Profile',
          },
          loadChildren: () =>
            import('@digital-platform/waste-profiles/feature-duplicate').then(
              (m) => m.WasteProfilesFeatureDuplicateModule
            ),
        },
        {
          path: 'waste-profiles/change',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'change:waste_profile',
            title: 'Change Waste Profile',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-change-wizard'
            ).then((m) => m.WasteProfilesFeatureChangeWizardModule),
        },
        {
          path: 'waste-profiles/recertify',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'recertify:waste_profile',
            title: 'Recertify Waste Profile',
            pageName: 'Recertify Waste Profile',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-recertify-wizard'
            ).then((m) => m.WasteProfilesFeatureRecertifyWizardModule),
        },

        {
          path: 'waste-profiles/change-recertify',
          canActivateChild: [AuthGuard],
          data: {
            permissions: ['recertify:waste_profile', 'change:waste_profile'],
            title: 'Change Recertify Waste Profile',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-change-recertify-wizard'
            ).then((m) => m.WasteProfilesFeatureChangeRecertifyWizardModule),
        },
        {
          path: 'waste-profiles/v2',
          canActivateChild: [AuthGuard],
          data: {
            permissions: ['create:waste_profile', 'update:waste_profile'],
            title: 'Waste Profiles',
          },
          loadChildren: () =>
            import('@digital-platform/waste-profiles/feature-wizard-v2').then(
              (m) => m.WasteProfilesFeatureWizardV2Module
            ),
        },
        {
          path: 'waste-profiles',
          canActivateChild: [AuthGuard],
          data: {
            permissions: ['create:waste_profile', 'update:waste_profile'],
            title: 'Waste Profiles',
          },
          loadChildren: () =>
            import('@digital-platform/waste-profiles/feature-wizard-v2').then(
              (m) => m.WasteProfilesFeatureWizardV2Module
            ),
        },
        {
          path: 'offline-submission',
          canActivateChild: [AuthGuard],
          data: {
            permissions: ['create_offline:waste_profile'],
            title: 'Offline Waste Profiles',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-offline-submission-wizard'
            ).then((m) => m.WasteProfilesFeatureOfflineSubmissionWizardModule),
        },
        {
          path: 'waste-profiles-status',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'update:waste_profile_status',
            title: 'Profile Status',
            pageName: 'Profile Status',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-status-list-page'
            ).then((m) => m.WasteProfilesFeatureStatusListPageModule),
        },
        {
          path: 'waste-profiles-status/approve',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'update:waste_profile_status',
            title: 'Approve Waste Profile',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-approve-page'
            ).then((m) => m.WasteProfilesFeatureApprovePageModule),
        },
        {
          path: 'waste-profiles-status/incomplete',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'update:waste_profile_status',
            title: 'Incomplete Waste Profile',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-incomplete-page'
            ).then((m) => m.WasteProfilesFeatureIncompletePageModule),
        },
        {
          path: 'waste-profiles-status/reject',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'update:waste_profile_status',
            title: 'Reject Waste Profile',
          },
          loadChildren: () =>
            import('@digital-platform/waste-profiles/feature-reject-page').then(
              (m) => m.WasteProfilesFeatureRejectPageModule
            ),
        },
        {
          path: 'search-waste-profile',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'search:waste_profile',
            title: 'Search Waste Profile',
            pageName: 'Waste Profile Admin',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-waste-profile-search'
            ).then((m) => m.WasteProfilesFeatureWasteProfileSearchModule),
        },
        {
          path: 'reporting-page',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'reporting:waste_profile',
            title: 'Reporting Waste Profile',
            pageName: 'Waste Activity Report',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-waste-profile-reporting'
            ).then((m) => m.WasteProfilesFeatureWasteProfileReportingModule),
        },
        {
          path: 'special-waste-view',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'view:legacy_waste_profile',
            title: 'View Waste Profile',
            pageName: 'View Waste Profile',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-special-waste-view'
            ).then((m) => m.WasteProfilesFeatureSpecialWasteViewModule),
        },
        {
          path: 'waste-profile/edit',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'admin_update:waste_profile',
            title: 'View Manual Edit Waste Profile',
            pageName: 'Edit Waste Profile',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-manual-waste-profile-edit-page'
            ).then(
              (m) => m.WasteProfilesFeatureManualWasteProfileEditPageModule
            ),
        },

        {
          path: 'update-metadata',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'update:waste_profile_metadata',
            title: 'Update Metadata',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-profiles/feature-waste-profile-metadata'
            ).then((m) => m.WasteProfilesFeatureWasteProfileMetadataModule),
        },
        {
          path: 'generators',
          data: {
            title: 'Generators',
          },
          pathMatch: 'full',
          redirectTo: 'generators/list',
        },
        {
          path: 'generators/list',
          pathMatch: 'full',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'list:generator',
            title: 'Generators',
            pageName: 'Generators',
          },
          loadChildren: () =>
            import('@digital-platform/generators/feature-list-page').then(
              (m) => m.GeneratorsFeatureListPageModule
            ),
        },
        {
          path: 'generators',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'create:generator',
            title: 'Create Generator',
          },
          loadChildren: () =>
            import('@digital-platform/generators/feature-wizard-page').then(
              (m) => m.GeneratorsFeatureWizardPageModule
            ),
        },
        {
          path: 'generators/edit/generator',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'update:generator',
            title: 'Edit Generator',
          },
          loadChildren: () =>
            import('@digital-platform/generators/feature-edit-generator').then(
              (m) => m.GeneratorsFeatureEditGeneratorModule
            ),
        },
        {
          path: 'generators/edit/billing',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'update:generator',
            title: 'Generator Edit Billing',
          },
          loadChildren: () =>
            import('@digital-platform/generators/feature-edit-billing').then(
              (m) => m.GeneratorsFeatureEditBillingModule
            ),
        },
        {
          path: 'services/request',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'create:service_request',
            title: 'Service Request',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-services/feature-request-wizard'
            ).then((m) => m.WasteServicesFeatureRequestWizardModule),
        },
        {
          path: 'customers',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'list:customer',
            title: 'Customers',
          },
          loadChildren: () =>
            import('@digital-platform/customers/feature-list').then(
              (m) => m.CustomersFeatureListModule
            ),
        },
        {
          path: 'admin-tools',
          //canActivateChild: [AuthGuard],
          data: {
            permissions: ['add:sales_rep', 'add:coordinator', 'add:analyst'],
            title: 'IT Admin Tools',
          },
          loadChildren: () =>
            import('@digital-platform/ries-hub-ui/feature-it-admin-tools').then(
              (m) => m.RiesHubUiFeatureItAdminToolsModule
            ),
        },
        {
          path: 'services',
          data: {
            title: 'Services',
          },
          redirectTo: 'services/summary',
          pathMatch: 'full',
        },
        {
          path: 'services/summary',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'list:service',
            title: 'Services',
            pageName: 'Services',
          },
          loadChildren: () =>
            import(
              '@digital-platform/waste-services/feature-services-summary-page'
            ).then((m) => m.WasteServicesFeatureServicesSummaryPageModule),
        },
        {
          path: 'state-requirements',
          canActivateChild: [AuthGuard],
          data: {
            role: 'Customer',
            title: 'State Requirements',
            pageName: 'State Requirements Library',
          },
          loadChildren: () =>
            import(
              '@digital-platform/ries-hub-ui/feature-state-requirements'
            ).then((m) => m.RiesHubUiFeatureStateRequirementsModule),
        },
        {
          path: 'profile-settings',
          // canActivateChild: [AuthGuard], // not needed, allowed by default but ui changes based on permissions
          data: {
            role: 'Customer',
            title: 'Profile Settings',
            pageName: 'Settings',
          },
          loadChildren: () =>
            import(
              '@digital-platform/ries-hub-ui/feature-profile-settings'
            ).then((m) => m.RiesHubUiFeatureProfileSettingsModule),
        },
        {
          path: 'authorization-forms',
          pathMatch: 'full',
          redirectTo: 'authorization-forms/summary',
        },
        {
          path: 'authorization-forms/summary',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'list:3pa',
            title: '3rd Party Authorizations',
            pageName: '3rd Party Authorizations',
          },
          loadChildren: () =>
            import(
              '@digital-platform/authorization-forms/feature-list-page'
            ).then((m) => m.AuthorizationFormsFeatureListPageModule),
        },
        {
          path: 'authorization-forms/submitted',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'view:3pa',
          },
          loadChildren: () =>
            import(
              '@digital-platform/authorization-forms/feature-submitted-page'
            ).then((m) => m.AuthorizationFormsFeatureSubmittedPageModule),
        },
        {
          path: 'authorization-forms',
          canActivateChild: [AuthGuard],
          data: {
            permissions: ['create:3pa', 'update:3pa'],
            title: '3rd Party Authorizations',
            pageName: '3rd Party Authorization',
          },
          loadChildren: () =>
            import(
              '@digital-platform/authorization-forms/feature-form-page'
            ).then((m) => m.AuthorizationFormsFeatureFormPageModule),
        },
        {
          path: 'service-agreements',
          pathMatch: 'full',
          redirectTo: 'service-agreements/summary',
        },
        {
          path: 'service-agreements/summary',
          pathMatch: 'full',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'list:service_agreement',
            title: 'Service Agreements',
          },
          loadChildren: () =>
            import(
              '@digital-platform/service-agreements/feature-summary-page'
            ).then((m) => m.ServiceAgreementsFeatureSummaryPageModule),
        },
        {
          path: 'service-agreements/edit',
          canActivateChild: [AuthGuard],
          data: {
            permissions: [
              'create:service_agreement',
              'update:service_agreement',
            ],
            title: 'Service Agreements',
          },
          loadChildren: () =>
            import('@digital-platform/service-agreements/feature-wizard').then(
              (m) => m.ServiceAgreementsFeatureWizardModule
            ),
        },
        {
          path: 'static',
          loadChildren: () =>
            import('@digital-platform/ries-hub-ui/feature-static-content').then(
              (m) => m.RiesHubUiFeatureStaticContentModule
            ),
        },
        {
          path: 'privacy-policy',
          data: {
            title: 'Privacy Policy',
          },
          redirectTo: 'static/privacy-policy',
        },
        {
          path: 'hub-user-guide',
          data: {
            title: 'Hub User Guide',
          },
          redirectTo: 'static/user-guide',
        },
        {
          path: 'terms-of-use',
          data: {
            title: 'Terms of Use',
          },
          redirectTo: 'static/terms-of-use',
        },
        {
          path: 'release-notes',
          redirectTo: 'static/release-notes',
        },
        {
          path: 'data-metrics',
          canActivateChild: [AuthGuard],
          data: {
            permission: 'view:metrics',
            title: 'Data Metrics',
          },
          loadChildren: () =>
            import('@digital-platform/ries-hub-ui/feature-metrics').then(
              (m) => m.RiesHubUiFeatureMetricsModule
            ),
        },
        {
          path: 'map',
          canActivateChild: [AuthGuard],
          data: {
            title: 'Assets',
            pageName: 'Assets',
          },
          loadChildren: () =>
            import('@digital-platform/ries-hub-ui/feature-map-page').then(
              (m) => m.RiesHubUiFeatureMapPageModule
            ),
        },
        {
          path: 'manifests/access-code',
          data: {
            title: 'Access Code',
          },
          component: AccessCodeComponent,
        },
        {
          path: 'manifests',
          pathMatch: 'full',
          redirectTo: 'manifests/summary',
        },
        {
          path: 'manifests/summary',
          canActivateChild: [AuthGuard],
          loadChildren: () =>
            import('@digital-platform/manifests').then(
              (m) => m.ManifestsModule
            ),
        },
        {
          path: 'access-denied',
          data: {
            title: 'Access Denied',
            pageName: 'Access Denied',
          },
          component: AccessDeniedComponent,
        },
        {
          path: 'server-error',
          data: {
            title: 'Server Error',
          },
          component: ServerErrorComponent,
        },
        {
          path: '**',
          data: {
            title: 'Not Found',
          },
          component: NotFoundComponent,
        },
      ],
    },
  ];
}
